import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { CalendarCheck, Check, Pencil, Trash2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

// ── Types ────────────────────────────────────────────────────────────────────

type AttendanceStatus = 'present' | 'absent' | 'half_day' | 'wfh' | 'holiday' | 'on_leave'

interface ShiftType {
  id: number
  name: string
  startTime: string
  endTime: string
}

/** Matches the nested shape returned by the backend listAttendance JOIN */
interface AttendanceRow {
  attendance: {
    id: number
    employeeId: number
    date: string
    status: string
    checkIn: string | null
    checkOut: string | null
    shiftTypeId: number | null
    overtimeHours: string | null
    lateMinutes: number | null
    earlyLeaveMinutes: number | null
    notes: string | null
    source: string | null
    createdAt: string
    updatedAt: string
  }
  employee: {
    id: number
    firstName: string
    lastName: string | null
    employeeNumber: string | null
  } | null
  shiftType: {
    id: number
    name: string
    startTime: string
    endTime: string
  } | null
}

/** Nested shape from listAttendanceRequests JOIN */
interface AttendanceRequestRow {
  request: {
    id: number
    employeeId: number
    date: string
    requestedStatus: string
    reason: string | null
    status: string
    approvedBy: number | null
    approvedAt: string | null
    createdAt: string
  }
  employee: {
    id: number
    firstName: string
    lastName: string | null
    employeeNumber: string | null
  } | null
}

const STATUS_CONFIG: Record<AttendanceStatus, { label: string; className: string }> = {
  present: { label: 'Present', className: 'bg-green-100 text-green-800 border-green-200' },
  absent: { label: 'Absent', className: 'bg-red-100 text-red-800 border-red-200' },
  half_day: { label: 'Half Day', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  wfh: { label: 'WFH', className: 'bg-blue-100 text-blue-800 border-blue-200' },
  holiday: { label: 'Holiday', className: 'bg-purple-100 text-purple-800 border-purple-200' },
  on_leave: { label: 'On Leave', className: 'bg-gray-100 text-gray-700 border-gray-200' },
}

function AttendanceStatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status as AttendanceStatus] ?? {
    label: status,
    className: 'bg-gray-100 text-gray-700',
  }
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  )
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function getDefaultDateRange() {
  const now = new Date()
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  return {
    from: firstDay.toISOString().split('T')[0],
    to: lastDay.toISOString().split('T')[0],
  }
}

function formatTime(value: string | null | undefined): string {
  if (!value) return '-'
  try {
    const d = new Date(value)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return value
  }
}

function isoToLocalTime(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    const hh = String(d.getHours()).padStart(2, '0')
    const mm = String(d.getMinutes()).padStart(2, '0')
    return `${hh}:${mm}`
  } catch {
    return ''
  }
}

// ── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/hr/attendance/')({
  component: AttendancePage,
})

function AttendancePage() {
  const defaultRange = getDefaultDateRange()
  const [dateFrom, setDateFrom] = useState(defaultRange.from)
  const [dateTo, setDateTo] = useState(defaultRange.to)
  const [employeeFilter, setEmployeeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [editing, setEditing] = useState<AttendanceRow | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({
    open: false,
    id: null,
  })
  const limit = 20

  const {
    data: attendanceData,
    isLoading,
    refetch: refetchAttendance,
  } = useQuery(
    orpc.hr.listAttendance.queryOptions({
      input: {
        page,
        limit,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        employeeId: employeeFilter !== 'all' ? Number(employeeFilter) : undefined,
        status: statusFilter !== 'all' ? (statusFilter as AttendanceStatus) : undefined,
      },
    }),
  )

  const { data: employeesData } = useQuery(
    orpc.hr.getEmployees.queryOptions({ input: { page: 1, limit: 500 } }),
  )

  const { data: shiftTypesData } = useQuery(
    orpc.hr.listShiftTypes.queryOptions({ input: {} }),
  )

  const { data: requestsData, refetch: refetchRequests } = useQuery(
    orpc.hr.listAttendanceRequests.queryOptions({
      input: { page: 1, limit: 50 },
    }),
  )

  const approveMutation = useMutation(
    orpc.hr.approveAttendanceRequest.mutationOptions({
      onSuccess: () => {
        toast.success('Request approved')
        refetchRequests()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const rejectMutation = useMutation(
    orpc.hr.rejectAttendanceRequest.mutationOptions({
      onSuccess: () => {
        toast.success('Request rejected')
        refetchRequests()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const deleteMutation = useMutation(
    orpc.hr.deleteAttendance.mutationOptions({
      onSuccess: () => {
        toast.success('Attendance deleted')
        refetchAttendance()
        setDeleteDialog({ open: false, id: null })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const rawData = attendanceData as { data?: unknown[]; total?: number } | null | undefined
  const rows = (rawData?.data ?? []) as AttendanceRow[]
  const total = rawData?.total ?? 0
  const employees = (employeesData?.data ?? []) as { id: number; firstName: string; lastName: string }[]
  const shiftTypes = (shiftTypesData ?? []) as ShiftType[]

  const rawRequests = requestsData as { data?: unknown[] } | null | undefined
  const requestRows = (rawRequests?.data ?? []) as AttendanceRequestRow[]
  const pendingRequests = requestRows.filter((r) => r.request.status === 'pending')

  const summary = rows.reduce(
    (acc, row) => {
      const s = row.attendance.status as AttendanceStatus
      acc[s] = (acc[s] ?? 0) + 1
      return acc
    },
    {} as Record<AttendanceStatus, number>,
  )

  const totalPages = Math.ceil(total / limit)

  const handleDelete = () => {
    if (deleteDialog.id) {
      deleteMutation.mutate({ id: deleteDialog.id })
    }
  }

  return (
    <div className="container mx-auto py-6 px-4">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Attendance</h1>
          <p className="text-muted-foreground">Track and manage employee attendance</p>
        </div>
        <Button asChild>
          <Link to="/hr/attendance/mark">
            <CalendarCheck className="h-4 w-4 mr-2" />
            Mark Attendance
          </Link>
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {(Object.keys(STATUS_CONFIG) as AttendanceStatus[]).map((s) => (
          <Card key={s} className="text-center">
            <CardHeader className="pb-1 pt-3 px-3">
              <CardTitle className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                {STATUS_CONFIG[s].label}
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-3 px-3">
              <span className="text-2xl font-bold">{summary[s] ?? 0}</span>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3 flex-wrap">
            <div className="flex gap-2 items-center">
              <span className="text-sm text-muted-foreground whitespace-nowrap">From</span>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value)
                  setPage(1)
                }}
                className="w-[160px]"
              />
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-sm text-muted-foreground whitespace-nowrap">To</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value)
                  setPage(1)
                }}
                className="w-[160px]"
              />
            </div>
            <Select
              value={employeeFilter}
              onValueChange={(v) => {
                setEmployeeFilter(v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id.toString()}>
                    {emp.firstName} {emp.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {(Object.keys(STATUS_CONFIG) as AttendanceStatus[]).map((s) => (
                  <SelectItem key={s} value={s}>
                    {STATUS_CONFIG[s].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Attendance Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12">
              <CalendarCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No attendance records found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Shift</TableHead>
                  <TableHead>Check-in</TableHead>
                  <TableHead>Check-out</TableHead>
                  <TableHead>Overtime</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.attendance.id}>
                    <TableCell className="font-medium">
                      {row.employee
                        ? `${row.employee.firstName} ${row.employee.lastName ?? ''}`
                        : `Employee #${row.attendance.employeeId}`}
                    </TableCell>
                    <TableCell>
                      {row.attendance.date
                        ? new Date(row.attendance.date).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <AttendanceStatusBadge status={row.attendance.status} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.shiftType
                        ? `${row.shiftType.name} (${row.shiftType.startTime}–${row.shiftType.endTime})`
                        : '-'}
                    </TableCell>
                    <TableCell>{formatTime(row.attendance.checkIn)}</TableCell>
                    <TableCell>{formatTime(row.attendance.checkOut)}</TableCell>
                    <TableCell>
                      {row.attendance.overtimeHours &&
                      parseFloat(row.attendance.overtimeHours) > 0
                        ? `${row.attendance.overtimeHours}h`
                        : '-'}
                    </TableCell>
                    <TableCell className="max-w-[180px] truncate text-muted-foreground">
                      {row.attendance.notes ?? '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing(row)}
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                          onClick={() =>
                            setDeleteDialog({ open: true, id: row.attendance.id })
                          }
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({total} records)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Pending Attendance Requests */}
      {pendingRequests.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-3">
            Pending Attendance Requests ({pendingRequests.length})
          </h2>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Requested Status</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingRequests.map((row) => (
                    <TableRow key={row.request.id}>
                      <TableCell>
                        {row.employee
                          ? `${row.employee.firstName} ${row.employee.lastName ?? ''}`
                          : `Employee #${row.request.employeeId}`}
                      </TableCell>
                      <TableCell>
                        {row.request.date
                          ? new Date(row.request.date).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <AttendanceStatusBadge status={row.request.requestedStatus} />
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {row.request.reason ?? '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600"
                            onClick={() => approveMutation.mutate({ id: row.request.id })}
                            disabled={approveMutation.isPending}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                            onClick={() => rejectMutation.mutate({ id: row.request.id })}
                            disabled={rejectMutation.isPending}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Dialog */}
      <AttendanceEditDialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null)
        }}
        row={editing}
        shiftTypes={shiftTypes}
        onSaved={() => {
          refetchAttendance()
          setEditing(null)
        }}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, id: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Attendance Record</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this attendance record? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// ── Edit Dialog ──────────────────────────────────────────────────────────────

interface AttendanceEditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  row: AttendanceRow | null
  shiftTypes: ShiftType[]
  onSaved: () => void
}

function AttendanceEditDialog({
  open,
  onOpenChange,
  row,
  shiftTypes,
  onSaved,
}: AttendanceEditDialogProps) {
  const [status, setStatus] = useState<AttendanceStatus>('present')
  const [checkIn, setCheckIn] = useState('')
  const [checkOut, setCheckOut] = useState('')
  const [overtimeHours, setOvertimeHours] = useState('')
  const [notes, setNotes] = useState('')
  const [shiftTypeId, setShiftTypeId] = useState<number | null>(null)

  // Reset form when the target row changes.
  useEffect(() => {
    if (row) {
      const a = row.attendance
      setStatus(a.status as AttendanceStatus)
      setCheckIn(isoToLocalTime(a.checkIn))
      setCheckOut(isoToLocalTime(a.checkOut))
      setOvertimeHours(
        a.overtimeHours && parseFloat(a.overtimeHours) > 0 ? a.overtimeHours : '',
      )
      setNotes(a.notes ?? '')
      setShiftTypeId(a.shiftTypeId)
    }
  }, [row])

  const saveMutation = useMutation(
    orpc.hr.markAttendance.mutationOptions({
      onSuccess: () => {
        toast.success('Attendance updated')
        onSaved()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  if (!row) return null

  const employeeName = row.employee
    ? `${row.employee.firstName} ${row.employee.lastName ?? ''}`
    : `Employee #${row.attendance.employeeId}`

  const dateStr = row.attendance.date

  const handleSave = () => {
    if (checkIn && checkOut && checkOut <= checkIn) {
      toast.error('Check-out must be after check-in')
      return
    }
    saveMutation.mutate({
      employeeId: row.attendance.employeeId,
      date: dateStr,
      status,
      checkIn: checkIn ? `${dateStr}T${checkIn}:00` : undefined,
      checkOut: checkOut ? `${dateStr}T${checkOut}:00` : undefined,
      shiftTypeId: shiftTypeId ?? undefined,
      overtimeHours: overtimeHours || undefined,
      notes: notes || undefined,
    })
  }

  const applyShiftPreset = (id: number) => {
    const st = shiftTypes.find((s) => s.id === id)
    if (!st) return
    setShiftTypeId(id)
    setCheckIn(st.startTime)
    setCheckOut(st.endTime)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Edit Attendance</DialogTitle>
          <DialogDescription>
            {employeeName} — {new Date(dateStr).toLocaleDateString()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Status */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Status</label>
            <Select value={status} onValueChange={(v) => setStatus(v as AttendanceStatus)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(STATUS_CONFIG) as AttendanceStatus[]).map((s) => (
                  <SelectItem key={s} value={s}>
                    {STATUS_CONFIG[s].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Shift */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Shift</label>
            <Select
              value={shiftTypeId?.toString() ?? 'none'}
              onValueChange={(v) => {
                if (v === 'none') {
                  setShiftTypeId(null)
                } else {
                  applyShiftPreset(Number(v))
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="No shift" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No shift</SelectItem>
                {shiftTypes.map((st) => (
                  <SelectItem key={st.id} value={st.id.toString()}>
                    {st.name} ({st.startTime}–{st.endTime})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Selecting a shift fills in check-in/out with that shift's hours.
            </p>
          </div>

          {/* Times + Overtime */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Check-in</label>
              <Input
                type="time"
                value={checkIn}
                onChange={(e) => setCheckIn(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Check-out</label>
              <Input
                type="time"
                value={checkOut}
                onChange={(e) => setCheckOut(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Overtime (hrs)</label>
              <Input
                type="number"
                min={0}
                step={0.25}
                placeholder="0"
                value={overtimeHours}
                onChange={(e) => setOvertimeHours(e.target.value)}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Notes</label>
            <Textarea
              placeholder="Optional notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="min-h-[80px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving...' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
